/**
 * Camera Presets Configuration
 *
 * Each breakpoint tier defines:
 *   default       — home/initial view
 *   cinematic     — weapon release flyby camera
 *   SEC001…SEC006 — per-section camera
 *
 * Per entry, use EITHER:
 *   { posNode, tarNode }  — looks up a named <group> in the GLB scene
 *   { pos, tar }          — raw world-space [x, y, z] arrays used directly
 *
 * resolveCamera(scene, entry) handles both forms and returns
 * { posWorld: THREE.Vector3, tarWorld: THREE.Vector3 } | null
 *
 * To tune mobile portrait, edit the mobile_portrait block.
 * To add a new breakpoint tier, add a matching key and entries here,
 * then update getPresetKey() below.
 */

import * as THREE from 'three'

// ---------------------------------------------------------------------------
// Preset data
// ---------------------------------------------------------------------------

export const CAMERA_PRESETS = {

  // --------------------------------------------------------------------------
  // Desktop Large — base / production (1920–2559px landscape)
  // --------------------------------------------------------------------------
  desktop: {
    default:    { pos: [165, 78, -111],   tar: [0, 0, 0] },
    cinematic:  { pos: [265, 30, -300],   tar: [50, -100, 0] },
    SEC001:   { posNode: 'POS001', tarNode: 'TAR001' },
    SEC002:   { posNode: 'POS002', tarNode: 'TAR002' },
    SEC003:   { posNode: 'POS003', tarNode: 'TAR003' },
    SEC004:   { posNode: 'POS004', tarNode: 'TAR004' },
    SEC005:   { posNode: 'POS005', tarNode: 'TAR005' },
    SEC006:   { posNode: 'POS006', tarNode: 'TAR006' },
  },

  // --------------------------------------------------------------------------
  // Tablet Landscape (900–1366px landscape)
  // --------------------------------------------------------------------------
  tablet_landscape: {
    default:    { pos: [165, 78, -111],   tar: [0, 0, 0] },
    cinematic:  { pos: [265, 30, -300],   tar: [50, -100, 0] },
    SEC001:   { posNode: 'POS001', tarNode: 'TAR001' },
    SEC002:   { posNode: 'POS002', tarNode: 'TAR002' },
    SEC003:   { posNode: 'POS003', tarNode: 'TAR003' },
    SEC004:   { posNode: 'POS004', tarNode: 'TAR004' },
    SEC005:   { posNode: 'POS005', tarNode: 'TAR005' },
    SEC006:   { posNode: 'POS006', tarNode: 'TAR006' },
  },

  // --------------------------------------------------------------------------
  // Tablet Portrait (768–1024px portrait)
  // --------------------------------------------------------------------------
  tablet_portrait: {
    default:    { pos: [165, 95, -111],   tar: [0, 0, 0] },
    cinematic:  { pos: [265, 30, -300],   tar: [50, -100, 0] },
    SEC001:   { posNode: 'POS001', tarNode: 'TAR001' },
    SEC002:   { posNode: 'POS002', tarNode: 'TAR002' },
    SEC003:   { posNode: 'POS003', tarNode: 'TAR003' },
    SEC004:   { posNode: 'POS004', tarNode: 'TAR004' },
    SEC005:   { posNode: 'POS005', tarNode: 'TAR005' },
    SEC006:   { posNode: 'POS006', tarNode: 'TAR006' },
  },

  // --------------------------------------------------------------------------
  // Phone Landscape (<900px landscape)
  // --------------------------------------------------------------------------
  phone_landscape: {
    default:    { pos: [165, 78, -111],   tar: [0, 0, 0] },
    cinematic:  { pos: [265, 30, -300],   tar: [50, -100, 0] },
    SEC001:   { posNode: 'POS001', tarNode: 'TAR001' },
    SEC002:   { posNode: 'POS002', tarNode: 'TAR002' },
    SEC003:   { posNode: 'POS003', tarNode: 'TAR003' },
    SEC004:   { posNode: 'POS004', tarNode: 'TAR004' },
    SEC005:   { posNode: 'POS005', tarNode: 'TAR005' },
    SEC006:   { posNode: 'POS006', tarNode: 'TAR006' },
  },

  // --------------------------------------------------------------------------
  // Mobile Portrait (<768px portrait)
  //
  // Use posNode/tarNode to reuse existing GLB nodes, OR
  // use pos/tar with raw world-space coordinates to position freely.
  //
  // Example using raw coords:
  //   SEC002: { pos: [14, 5, -6], tar: [5, 0, 0] },
  // --------------------------------------------------------------------------
  mobile_portrait: {
    default:    { pos: [60, 180, 0],                 tar: [0, 0, 0] },
    cinematic:  { pos: [180, 30, -180],              tar: [40, -50, 0] },  // tune me
    SEC001:   { pos: [40, -30.416, -36.074],         tar: [12.584, -8, -8.224] },       // Weapons Systems
    SEC002:   { pos: [134.853, 37.662, -51.576],     tar: [39.995, 2.5, -0.413] },      // General Systems
    SEC003:   { pos: [-200, 80, -100],               tar: [0, -40, 0] },                // Engine Systems
    SEC004:   { pos: [151.439, -55.692, 68.985],     tar: [6.696, 0, -0] },             // Flight Systems
    SEC005:   { pos: [110.0, -60, 60],               tar: [16.019, -12, -0.146] },      // Landing Systems
    SEC006:   { pos: [6, -15, -21],      tar: [5.989, -19.00, -7.958] },   // Weapon Stores
  },
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Return the preset key for the given device/orientation.
 */
export function getPresetKey(deviceType, orientation) {
  if (deviceType === 'desktop') return 'desktop'
  if (deviceType === 'phone' && orientation === 'portrait') return 'mobile_portrait'
  if (deviceType === 'phone' && orientation === 'landscape') return 'phone_landscape'
  return `${deviceType}_${orientation}`
}

/**
 * Return the full preset object for the given device/orientation.
 * Falls back to desktop if the key is not found.
 */
export function getCameraPreset(deviceType, orientation) {
  const key = getPresetKey(deviceType, orientation)
  return CAMERA_PRESETS[key] || CAMERA_PRESETS.desktop
}

/**
 * Resolve a preset entry (posNode/tarNode or pos/tar) into world-space Vectors.
 *
 * @param {THREE.Scene} scene   - The R3F scene, used to look up named nodes
 * @param {object}      entry   - A preset entry: { posNode, tarNode } or { pos, tar }
 * @returns {{ posWorld: THREE.Vector3, tarWorld: THREE.Vector3 } | null}
 */
export function resolveCamera(scene, entry) {
  if (!entry) return null

  let posWorld, tarWorld

  if (entry.posNode && entry.tarNode) {
    // Node-based lookup
    const posObj = scene.getObjectByName(entry.posNode)
    const tarObj = scene.getObjectByName(entry.tarNode)
    if (!posObj || !tarObj) return null
    posObj.updateMatrixWorld(true)
    tarObj.updateMatrixWorld(true)
    posWorld = posObj.getWorldPosition(new THREE.Vector3())
    tarWorld = tarObj.getWorldPosition(new THREE.Vector3())
  } else if (entry.pos && entry.tar) {
    // Raw world-space coords
    posWorld = new THREE.Vector3(...entry.pos)
    tarWorld = new THREE.Vector3(...entry.tar)
  } else {
    return null
  }

  return { posWorld, tarWorld }
}

/**
 * Get SEC-specific entry from the active preset, falling back to desktop.
 */
export function getSecEntry(preset, secId) {
  return preset[secId] || CAMERA_PRESETS.desktop[secId] || null
}
